import { AiOutlineDashboard, AiOutlineLoading } from "react-icons/ai";
import {
  Alert,
  Badge,
  Button,
  Card,
  Modal,
  ModalBody,
  ModalHeader,
  Tabs,
} from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { ADMIN, CATEGORIES, SELLER } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import { useNavigate, useParams } from "react-router-dom";
import DrawerLoading from "../components/DrawerLoading";
import moment from "moment";
import { FaUserFriends } from "react-icons/fa";
import AdminHistoryTableSpecific from "../components/tables/AdminHistoryTableSpecific";

const CompanyDetailsSellerPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { id } = useParams();
  const { userPermission, user } = useApplication();

  // Se quiser garantir que só vendedor veja esta página, descomente:
  // if (user?.permissionId !== "SELLER") return <Alert color="failure">Acesso restrito ao perfil Vendedor.</Alert>;

  const {
    isLoading: isLoadingCompany,
    error: errorCompany,
    data: company,
  } = useQuery(["company", id], async () => {
    if (!id) return null;
    const { data } = await axios.get(
      `${SELLER.GET_COMPANY_BY_INFO}/${id}?type=id`
    );
    return data.data;
  });

  const {
    isLoading: isLoadingCompanyQueries,
    error: errorCompanyQueries,
    data: companyQueries,
  } = useQuery(
    ["companyQueries", company?.id],
    async () => {
      const { data: items } = await axios.get(
        `${SELLER.GET_COMPANY_QUERIES}/${company?.id}`
      );
      return items.data;
    },
    { enabled: !!company?.id }
  );

  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );
    return data.data.data;
  });

  // ---- Helpers ----
  const fmtBR = (d) =>
    d && moment(d).isValid() ? moment(d).format("DD/MM/YYYY") : "—";
  const daysSince = (d) =>
    d && moment(d).isValid() ? moment().diff(moment(d), "days") : null;
  const daysUntil = (d) =>
    d && moment(d).isValid() ? moment(d).diff(moment(), "days") : null;

  const statusNum = Number(company?.status);
  const STATUS_STYLES = {
    1: {
      gradient: "from-green-400 to-green-600",
      badge: "success",
      label: "Ativo",
    },
    0: {
      gradient: "from-red-400 to-red-600",
      badge: "failure",
      label: "Bloqueado",
    },
    "-1": {
      gradient: "from-red-400 to-red-600",
      badge: "failure",
      label: "Inadimplente",
    },
  };
  const style = STATUS_STYLES[statusNum] || STATUS_STYLES["-1"];

  const createdDays = daysSince(company?.createdAt);
  const endDays = daysUntil(company?.dateEndContract);

  // Agrupar consultas por categoria (sem preços e sem configurações)
  const grouped = useMemo(() => {
    return (categories || []).map((category) => {
      const items = (companyQueries || []).filter(
        (q) => q.categoryId === category.id
      );
      const mains = items.filter((q) => !q.isAdditional);
      const adds = items.filter((q) => !!q.isAdditional);
      return { ...category, mains, adds };
    });
  }, [categories, companyQueries]);

  // ---- Loading/Erro ----
  if (isLoadingCompany) {
    return (
      <div className="bg-white p-4 rounded-md shadow-md">
        <DrawerLoading />
      </div>
    );
  }

  if (errorCompany) {
    return (
      <div className="bg-white p-4 rounded-md shadow-md">
        <Alert color="failure">
          Não foi possível carregar os dados da empresa.
        </Alert>
        <Button onClick={() => navigate(-1)} size="xs" className="mt-2">
          Voltar
        </Button>
      </div>
    );
  }

  // ---- Permissão: responsável diferente ----
  const noPermission = company && user && company.responsibleId !== user.id;

  if (noPermission) {
    return (
      <>
        <Button
          onClick={() => navigate(-1)}
          size="xs"
          className="w-fit bg-gray-500 px-6 mb-2"
        >
          Voltar
        </Button>
        <div className="bg-white px-4 py-4 rounded-md shadow-md">
          <Alert color="failure">
            <div className="font-semibold">Acesso negado</div>
            <div className="text-sm">
              Você não tem permissão para visualizar este cliente, pois não é o
              responsável vinculado a ele.
            </div>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Button
        onClick={() => navigate(-1)}
        size="xs"
        className="w-fit bg-gray-500 px-6 mb-2"
      >
        Voltar
      </Button>

      <div className="bg-white px-4 py-4 rounded-md shadow-md relative">
        <Tabs
          aria-label="Seller tabs"
          id="tab-company-details"
          activeTab={activeTab}
          onActiveTabChange={(i) => setActiveTab(i)}
        >
          <Tabs.Item title="Resumo" icon={AiOutlineDashboard}>
            {/* Header básico */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
              <div>
                <div className="text-xl font-semibold text-gray-900">
                  {company?.companyTypeId === "REPRESENTANTE"
                    ? "Representante"
                    : "Cliente"}{" "}
                  · {company?.name || company?.socialReason}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Representante Pai:</span>{" "}
                  {company?.ParentCompany?.name ||
                    company?.ParentCompany?.socialReason ||
                    "—"}
                </div>
              </div>
            </div>

            {/* Alertas leves (sem financeiro detalhado) */}
            <div className="grid grid-cols-1 gap-3">
              {company?.status === 2 && (
                <Alert color="failure">
                  Cliente <b>inadimplente</b>. Algumas funções podem estar
                  restritas.
                </Alert>
              )}
              {Array.isArray(companyQueries) && companyQueries.length === 0 && (
                <Alert color="gray">
                  Nenhuma consulta ativa neste cliente. Verifique com o
                  administrador.
                </Alert>
              )}
            </div>

            {/* Cards principais (valores ocultos) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
              <div
                className={`p-6 rounded-xl shadow-md text-white bg-gradient-to-br ${style.gradient}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium opacity-90">Status</span>
                  <Badge color={style.badge} size="xs">
                    {statusNum === 1 ? "OK" : "Atenção"}
                  </Badge>
                </div>
                <div className="mt-3 text-2xl font-bold">{style.label}</div>
                <div className="mt-1 text-xs opacity-90">
                  Plano:{" "}
                  <b>
                    {company?.typePayment === "PRE" ? "Pré-pago" : "Pós-pago"}
                  </b>
                </div>
              </div>

              {/* Créditos/VCM (mascarado para vendedor) */}
              <div className="p-6 rounded-xl shadow-md bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <span className="text-sm font-medium opacity-90">
                  💳 Crédito / VCM
                </span>
                <div className="mt-3 text-2xl font-bold">—</div>
                <div className="mt-1 text-xs opacity-80">
                  Valores ocultos para o perfil Vendedor
                </div>
              </div>

              {/* Total utilizado (mascarado) */}
              <div className="p-6 rounded-xl shadow-md bg-gradient-to-br from-pink-400 to-red-500 text-white">
                <span className="text-sm font-medium opacity-90">
                  📊 Total utilizado
                </span>
                <div className="mt-3 text-2xl font-bold">—</div>
                <div className="mt-1 text-xs opacity-80">
                  Valores ocultos para o perfil Vendedor
                </div>
              </div>
            </div>

            {/* Dados rápidos (somente informativos) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4 text-sm text-gray-700">
                {isLoadingCompany && <DrawerLoading />}
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  🧾 Dados rápidos
                </h2>

                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Nome/Razão Social</p>
                    <p className="font-medium break-words">
                      {company?.name || company?.socialReason || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Documento</p>
                    <p className="font-medium break-words">
                      {company?.document || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Telefone</p>
                    <p className="font-medium break-words">
                      {company?.phoneNumber || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">E-mail</p>
                    <p className="font-medium break-words">
                      {company?.email || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Cliente desde:</p>
                    <p className="font-medium break-words">
                      {fmtBR(company?.createdAt)}
                      {daysSince(company?.createdAt) !== null && (
                        <span className="ml-2 text-xs text-gray-500">
                          {createdDays === 0
                            ? "(hoje)"
                            : createdDays === 1
                            ? "(há 1 dia)"
                            : `(há ${createdDays} dias)`}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Término de contrato</p>
                    <p className="font-medium break-words">
                      {fmtBR(company?.dateEndContract)}
                      {endDays !== null && (
                        <span
                          className={`ml-2 text-xs ${
                            endDays < 0 ? "text-red-600" : "text-gray-500"
                          }`}
                        >
                          {endDays === 0
                            ? "(hoje)"
                            : endDays > 0
                            ? `(${endDays} ${
                                endDays === 1 ? "dia" : "dias"
                              } restantes)`
                            : `(vencido há ${Math.abs(endDays)} ${
                                Math.abs(endDays) === 1 ? "dia" : "dias"
                              })`}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Termos aceitos em:</p>
                    <p className="font-medium break-words">
                      {company?.termsAcceptedAt ? (
                        moment(company?.termsAcceptedAt)?.format(
                          "DD/MM/yyyy HH:mm"
                        )
                      ) : (
                        <span className="text-red-500 font-semibold bg-red-50 px-1 rounded-md">
                          Não aceito
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      Mostrar logo nas consultas?
                    </p>
                    <p className="font-medium break-words">
                      {company?.showLogo ? "Sim" : "Não"}
                    </p>
                  </div>
                </div>
              </section>

              {/* Timeline simplificada (sem pagamentos) */}
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  🕓 Últimas atividades
                </h2>
                <Alert color="gray">
                  Atividades financeiras ocultas para o perfil Vendedor.
                </Alert>
                <div className="text-xs text-gray-500 mt-2">
                  Consulte o administrador para detalhes de cobrança e crédito.
                </div>
              </section>

              {/* Consultas ativas (sem preços, sem config) */}
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4 relative">
                {(isLoadingCompanyQueries || isLoadingCategories) && (
                  <DrawerLoading />
                )}
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  📄 Consultas ativas
                </h2>

                {/* Busca local */}
                <div className="mb-2">
                  <input
                    placeholder="Buscar consulta por nome…"
                    onChange={(e) => {
                      const q = (e.target.value || "").toLowerCase();
                      const rows = document.querySelectorAll(".query-row");
                      rows.forEach((li) => {
                        const name = (
                          li.getAttribute("data-name") || ""
                        ).toLowerCase();
                        li.style.display = name.includes(q) ? "" : "none";
                      });
                    }}
                    className="bg-gray-50 rounded-md w-full p-2 border border-gray-300"
                  />
                </div>

                <div
                  className="overflow-y-auto pr-2"
                  style={{ maxHeight: 320 }}
                >
                  {(() => {
                    const any = grouped.some(
                      (g) => (g.mains?.length || 0) + (g.adds?.length || 0) > 0
                    );
                    if (!any)
                      return (
                        <Alert color="gray">Nenhuma consulta contratada.</Alert>
                      );

                    return (
                      <div className="space-y-4">
                        {grouped.map((cat) => (
                          <div key={cat.id} className="space-y-2">
                            <div className="font-semibold text-green-600 uppercase text-xs">
                              {cat.name}
                            </div>

                            {/* Principais */}
                            <div className="rounded-lg border border-gray-100">
                              <div className="px-2 py-1.5 bg-gray-50 border-b border-gray-100 rounded-t-lg">
                                <span className="text-[11px] font-semibold text-gray-600 tracking-wide">
                                  Consultas principais
                                </span>
                              </div>

                              {cat.mains?.length ? (
                                <ul className="divide-y">
                                  {cat.mains.map((q) => (
                                    <li
                                      key={`main-${q.id}`}
                                      className="query-row py-2 px-2 flex items-center justify-between gap-2"
                                      data-name={q?.name || ""}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-sm truncate">
                                          {q?.name}
                                        </span>
                                        {q?.QueryCustomizedId && (
                                          <Badge size="xs" color="info">
                                            Custom
                                          </Badge>
                                        )}
                                      </div>
                                      {/* Preço e engrenagem removidos para vendedor */}
                                      <div className="text-xs text-gray-400">
                                        acesso habilitado
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-gray-500 px-2 py-2">
                                  Sem itens principais.
                                </div>
                              )}
                            </div>

                            {/* Adicionais */}
                            <div className="rounded-lg border border-gray-100">
                              <div className="px-2 py-1.5 bg-emerald-50/60 border-b border-gray-100 rounded-t-lg">
                                <span className="text-[11px] font-semibold text-emerald-700 tracking-wide">
                                  Consultas adicionais
                                </span>
                              </div>

                              {cat.adds?.length ? (
                                <ul className="divide-y">
                                  {cat.adds.map((q) => (
                                    <li
                                      key={`add-${q.id}`}
                                      className="query-row py-2 px-2 flex items-center justify-between gap-2"
                                      data-name={q?.name || ""}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-sm truncate">
                                          {q?.name}
                                        </span>
                                        {q?.QueryCustomizedId && (
                                          <Badge size="xs" color="info">
                                            Custom
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        acesso habilitado
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-gray-500 px-2 py-2">
                                  Sem adicionais.
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </section>
            </div>
          </Tabs.Item>

          {/* Histórico de consultas (mantido para o vendedor, sem cifras/gestão) */}
          <Tabs.Item title="Consultas">
            <AdminHistoryTableSpecific
              companyId={company?.id}
              isActive={activeTab === 1}
            />
          </Tabs.Item>
        </Tabs>
      </div>
    </>
  );
};

export default CompanyDetailsSellerPage;
